import { usePrevious } from 'common';
import { isNumber } from 'lodash';
import { useLayoutEffect, useRef } from 'react';
import {
  BeforeCapture,
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  DropResult,
  Droppable,
  DroppableProvided,
  useKeyboardSensor,
  useMouseSensor,
} from 'react-beautiful-dnd';
import ReactDOM from 'react-dom';
import { AutoSizer, Index, List, ListRowProps, OverscanIndicesGetterParams, WindowScroller } from 'react-virtualized';
import { BreakpointProperties } from '../../../../../hooks/useBreakpoint';
import useTouchSensor from '../../../../../hooks/useTouchSensor';
import {
  CfDataGridForStoreDataProps,
  CfDataGridProps,
  CfDataGridRowType,
  CfDataGridTableViewDefinition,
  CfDraggableRowData,
  NavigableElement,
} from '../../../CfDataGridProps';
import { getHasItems, getIsLoading, getLastPage } from '../../../utility/data-grid-props';
import { overrideListScrollTo } from '../../../utility/scroll-to';
import { CfDataGridNoResultsFound } from '../../DataGridNoResultsFound/CfDataGridNoResultsFound';
import { GetDragDropRow } from '../row/drag-drop-helpers';
import {
  getScrollTo,
  getVirtualizedTableViewCellMeasurerCache,
  scrollToCache,
  setScrollTo,
} from './CfDataGridTableViewVirtualizedBody';
import { StyledGridBody } from './body.styles';
import { detectTableRowsChanged, getDraggableTableRows } from './common';

/* 
Caches
*/

export function CfDataGridTableViewDragDropBody<TItem, TAppState>(props: {
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>;
  tableViewDefinition: CfDataGridTableViewDefinition<TItem>;
  setNavigableElement: (navigableElement: NavigableElement, rowIndex: number, columnIndex: number) => void;
  breakpointProperties: BreakpointProperties;
  focusClosestEnabledElement: (rowIndex: number, elementIndex?: number) => void;
}): JSX.Element {
  /* 
  List 
  */
  const listRef = useRef<List | null>(null);

  /* 
  Constants 
  */
  const gridId = props.dataGridProps.id;
  const dataGridProps = props.dataGridProps;
  const dragDropProps = props.tableViewDefinition?.dragDropProps;
  const isLoading = getIsLoading(props.dataGridProps);
  const lastPage = getLastPage(props.dataGridProps);
  const scrollTo = getScrollTo(props.dataGridProps.id);
  const getRowHeight = dragDropProps?.getRowHeight;
  const isRowDraggable = dragDropProps?.isRowDraggable ?? true;

  /* 
  Functions 
  */
  const setListRef = (list: List | null, droppableProvided: DroppableProvided) => {
    listRef.current = list;
    if (list) {
      overrideListScrollTo(list);
      // eslint-disable-next-line react/no-find-dom-node
      const node = ReactDOM.findDOMNode(list);
      if (node instanceof HTMLElement) {
        droppableProvided.innerRef(node);
      }
    }
    dragDropProps?.scrollIndexRef?.(list ? (i: number) => setScrollTo(gridId, i) : null);
  };

  /* 
  Functions
  */
  const getFineRowHeight = (item: TItem | undefined, type: CfDataGridRowType) => {
    if (item && getRowHeight) return getRowHeight(item, type);
    return 0;
  };

  const { itemIds, rows, estimatedRowSize } = getDraggableTableRows(
    props.dataGridProps,
    props.tableViewDefinition,
    props.setNavigableElement,
    isRowDraggable,
    getFineRowHeight
  );

  const prevItemIds = usePrevious(itemIds);

  /* 
  Effects
  */
  const prevIsLoading = usePrevious(isLoading);

  useLayoutEffect(() => {
    if (!listRef.current || !lastPage || isLoading || scrollTo === undefined) return;

    // Handle scroll-to
    if (
      (prevIsLoading === undefined && isLoading === false) ||
      (lastPage.pageIndex === 0 && prevIsLoading != isLoading)
    ) {
      listRef.current?.scrollToPosition?.(scrollTo.scrollTop);
      props.focusClosestEnabledElement(scrollTo.itemIndex);
      scrollToCache.remove(gridId);
    }
  }, [isLoading, scrollTo]);

  useLayoutEffect(() => {
    if (!listRef.current || isLoading) return;

    listRef.current.recomputeRowHeights();
    setTimeout(() => {
      listRef.current?.recomputeRowHeights();
    }, 20);
  }, [props.breakpointProperties.breakpoint]);

  /* 
  Hooks
  */

  useLayoutEffect(() => {
    if (!isLoading) {
      const { differenceFound } = detectTableRowsChanged(itemIds, prevItemIds);
      const cache = getVirtualizedTableViewCellMeasurerCache(gridId, undefined);

      if (differenceFound) {
        cache.clearAll();

        listRef.current?.recomputeRowHeights();
      }
    }
  }, [isLoading, itemIds]);

  if (!getHasItems(dataGridProps)) {
    return (
      <StyledGridBody>
        <CfDataGridNoResultsFound
          message={props.dataGridProps.noItemsMessage}
          sx={props.dataGridProps.noItemsMessageSx}
          displayMode='table'
        />
      </StyledGridBody>
    );
  }

  function onBeforeCapture(before: BeforeCapture) {
    const destinationIndex = Number(before.draggableId);
    if (isNumber(destinationIndex)) {
      const sourceItem = rows[destinationIndex];
      if (sourceItem) dragDropProps?.onBeforeCapture?.(sourceItem);
    }
  }

  function onDragEnd(result: DropResult) {
    handleDragEnd(result);
    dragDropProps?.onDragEnd?.();
  }

  function handleDragEnd(result: DropResult) {
    if (result.combine) {
      const destinationIndex = Number(result.combine?.draggableId);
      if (isNumber(destinationIndex)) {
        const combineContext = {
          sourceItem: rows[result.source.index],
          destinationItem: rows[destinationIndex],
        };
        dragDropProps?.onCombine?.(combineContext);
      }
      return;
    }

    if (!result.destination) {
      return;
    }

    if (result.source.index !== result.destination.index) {
      const dropContext = {
        sourceItem: rows[result.source.index],
        previousItem: result.destination.index > 0 ? rows[result.destination.index - 1] : undefined,
        currentItem: result.destination.index >= 0 ? rows[result.destination.index] : undefined,
        nextItem: result.destination.index < rows.length - 1 ? rows[result.destination.index + 1] : undefined,
      };
      dragDropProps?.onDrop?.(dropContext);
    }
    return;
  }

  const listPropsBase = (droppableProvided: DroppableProvided) => ({
    rowCount: rows.length,
    rowHeight: ({ index }: Index) => rows[index].rowHeight,
    rowRenderer: getRowRender(rows),
    tabIndex: -1,
    ref: (ref: List) => setListRef(ref, droppableProvided),
    overscanRowCount: dragDropProps?.overscanRowCount ?? 25,
    estimatedRowSize: estimatedRowSize,
    overscanIndicesGetter: (params: OverscanIndicesGetterParams) => {
      return {
        overscanStartIndex: Math.max(0, params.startIndex - params.overscanCellsCount),
        overscanStopIndex: Math.min(params.cellCount - 1, params.stopIndex + params.overscanCellsCount),
      };
    },
  });

  const contained = dragDropProps?.contained ?? false;
  if (contained) {
    return (
      <DragDropContext
        onBeforeCapture={onBeforeCapture}
        onDragEnd={onDragEnd}
        sensors={[useTouchSensor, useKeyboardSensor, useMouseSensor]}
        enableDefaultSensors={false}
      >
        <Droppable
          isCombineEnabled={props.tableViewDefinition.dragDropProps?.onCombine !== undefined}
          droppableId='droppable'
          mode='virtual'
          renderClone={(provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: DraggableRubric) => {
            return GetDragDropRow(rows[rubric.source.index], provided, snapshot, rubric);
          }}
        >
          {(droppableProvided: DroppableProvided) => (
            <AutoSizer>
              {({ width, height }) => <List {...listPropsBase(droppableProvided)} width={width} height={height} />}
            </AutoSizer>
          )}
        </Droppable>
      </DragDropContext>
    );
  }
  return (
    <DragDropContext
      onBeforeCapture={onBeforeCapture}
      onDragEnd={onDragEnd}
      sensors={[useTouchSensor, useKeyboardSensor, useMouseSensor]}
      enableDefaultSensors={false}
    >
      <Droppable
        isCombineEnabled={props.tableViewDefinition.dragDropProps?.onCombine !== undefined}
        droppableId='droppable'
        mode='virtual'
        renderClone={(provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: DraggableRubric) => {
          return GetDragDropRow(rows[rubric.source.index], provided, snapshot, rubric);
        }}
      >
        {(droppableProvided: DroppableProvided) => (
          <WindowScroller>
            {({ height, isScrolling, onChildScroll, scrollTop }) => (
              <AutoSizer disableHeight>
                {({ width }) => (
                  <List
                    {...listPropsBase(droppableProvided)}
                    width={width}
                    height={height}
                    autoHeight
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    scrollTop={scrollTop}
                  />
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export const getRowRender = (rows: CfDraggableRowData[]) =>
  function rowRender(props: ListRowProps): JSX.Element | null {
    const row: CfDraggableRowData = rows[props.index];
    if (!row) return null;
    return (
      <Draggable draggableId={`${props.index}`} index={props.index} key={row.id} isDragDisabled={row.type !== 'row'}>
        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: DraggableRubric) => {
          return GetDragDropRow(row, provided, snapshot, rubric, props.style);
        }}
      </Draggable>
    );
  };
//
